import {
  useCompensationComponents,
  useCompensationComponentsSort,
} from "@/queries/use-compensation-components.query.ts";
import { ApiCompensationComponent } from "@/types/compensation-component.types.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import {
  Button,
  FontAwesomeIcon,
  Modal,
  ModalProps,
  Skeleton,
  Stack,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export function CompensationComponentsSortModal(props: ModalProps) {
  const { t } = useTranslation();
  const { close } = useModals();
  const { id } = props;
  const { data: components, isPending } = useCompensationComponents();
  const componentsSort = useCompensationComponentsSort();

  const syncKey = (components ?? []).map((component) => component.id).join(",");
  const [sortedComponents, setSortedComponents] = useState<ApiCompensationComponent[]>(
    components ?? []
  );
  const [syncedKey, setSyncedKey] = useState(syncKey);

  if (syncKey !== syncedKey) {
    setSyncedKey(syncKey);
    setSortedComponents(components ?? []);
  }

  const handleDragEnd = ({ source, destination }: DropResult) => {
    if (!destination || destination.index === source.index) return;

    const nextComponents = Array.from(sortedComponents);
    const [movedComponent] = nextComponents.splice(source.index, 1);
    nextComponents.splice(destination.index, 0, movedComponent);
    setSortedComponents(nextComponents);
  };

  const saveOrder = () => {
    componentsSort.mutate(
      sortedComponents.map((component) => component.id),
      {
        onSuccess: () => {
          analyticsService.trackEvent(analyticEvents.compensationTypes.sorted);
          close(id);
        },
      }
    );
  };

  return (
    <Modal
      {...props}
      title={t("common:reordering")}
      withCloser
      actions={
        <Stack>
          <Button size="lg" onClick={saveOrder} isLoading={componentsSort.isPending}>
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      {isPending ? (
        <Stack gap="md">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-4 w-48" />
          ))}
        </Stack>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="compensation-components-list" direction="vertical">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="w-full flex flex-col overflow-y-auto"
              >
                {sortedComponents.map((component, index) => (
                  <Draggable key={component.id} draggableId={component.id.toString()} index={index}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={provided.draggableProps.style}
                        className="flex items-center gap-4 bg-light py-2 min-h-16"
                      >
                        <div className="flex items-center justify-center cursor-grab text-secondary">
                          <FontAwesomeIcon icon="fa-light fa-grip-dots-vertical" />
                        </div>
                        <Title header={component.title} />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </Modal>
  );
}
